import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import './MealTickets.css';

interface MealTicketRecord {
  id: string;
  lastName: string;
  firstName: string;
  passType: string;
  quantity: number;
}

const MealTickets: React.FC = () => {
  const [records, setRecords] = useState<MealTicketRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMealTickets = async () => {
      try {
        setLoading(true);
        setError(null);
        const snapshot = await getDocs(collection(db, 'Registrations'));
        const ticketRecords: MealTicketRecord[] = [];

        snapshot.forEach(docSnap => {
          const data = docSnap.data();
          
          // Extract name - try multiple structures
          const primaryCamperName = data.registrationInfo?.primaryCamperName || '';
          const nameParts = primaryCamperName.split(' ');
          const firstName = nameParts[0] || data.firstName || '';
          const lastName = nameParts.slice(1).join(' ') || data.lastName || '';

          // Try new structure first: financial.otherFees.meals
          const meals = data.financial?.otherFees?.meals;
          
          // Then try legacy structure: mealFees
          const legacyMeals = data.mealFees;

          // Extract quantities from either structure
          let fullCampAdult = 0, fullCampChild = 0, dailyAdult = 0, dailyChild = 0;

          if (meals) {
            fullCampAdult = meals.fullCampPassesAdult?.quantity || 0;
            fullCampChild = meals.fullCampPassesChild?.quantity || 0;
            dailyAdult = meals.dailyPassesAdult?.quantity || 0;
            dailyChild = meals.dailyPassesChild?.quantity || 0;
          } else if (legacyMeals) {
            fullCampAdult = legacyMeals.fullCampPassesAdult || legacyMeals.fullCampPasses?.adult || 0;
            fullCampChild = legacyMeals.fullCampPassesChild || legacyMeals.fullCampPasses?.child || 0;
            dailyAdult = legacyMeals.dailyPassesAdult || legacyMeals.dailyPasses?.adult || 0;
            dailyChild = legacyMeals.dailyPassesChild || legacyMeals.dailyPasses?.child || 0;
          }

          // Add records for each pass type with quantity > 0
          if (fullCampAdult > 0) {
            ticketRecords.push({
              id: `${docSnap.id}-fca`,
              lastName,
              firstName,
              passType: 'Full Camp Pass - Adult',
              quantity: fullCampAdult
            });
          }

          if (fullCampChild > 0) {
            ticketRecords.push({
              id: `${docSnap.id}-fcc`,
              lastName,
              firstName,
              passType: 'Full Camp Pass - Child',
              quantity: fullCampChild
            });
          }

          if (dailyAdult > 0) {
            ticketRecords.push({
              id: `${docSnap.id}-da`,
              lastName,
              firstName,
              passType: 'Daily Pass - Adult',
              quantity: dailyAdult
            });
          }

          if (dailyChild > 0) {
            ticketRecords.push({
              id: `${docSnap.id}-dc`,
              lastName,
              firstName,
              passType: 'Daily Pass - Child',
              quantity: dailyChild
            });
          }
        });

        // Sort alphabetically by last name, then first name
        ticketRecords.sort((a, b) => {
          const lastNameCompare = a.lastName.localeCompare(b.lastName);
          if (lastNameCompare !== 0) return lastNameCompare;
          return a.firstName.localeCompare(b.firstName);
        });

        setRecords(ticketRecords);
      } catch (err) {
        console.error('Error fetching meal tickets:', err);
        setError('Failed to load meal tickets');
      } finally {
        setLoading(false);
      }
    };

    fetchMealTickets();
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = () => {
    const headers = ['Last Name', 'First Name', 'Quantity', 'Pass Type'];
    const escape = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;
    const csvRows = [
      headers.join(','),
      ...records.map(record =>
        [escape(record.lastName), escape(record.firstName), escape(record.quantity), escape(record.passType)].join(',')
      ),
    ];
    const csvContent = csvRows.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `meal-tickets-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) return <div>Loading meal tickets...</div>;
  if (error) return <div className="error">{error}</div>;

  // Calculate totals for summary
  const totals = records.reduce((acc, record) => {
    acc[record.passType] = (acc[record.passType] || 0) + record.quantity;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="meal-tickets-report">
      <div className="report-header">
        <h2>Meal Tickets</h2>
        <div className="report-actions">
          <span className="record-count">{records.length} entries</span>
          {records.length > 0 && (
            <button onClick={handleExportCSV} className="print-button">
              Export to CSV
            </button>
          )}
          <button onClick={handlePrint} className="print-button">
            🖨️ Print Report
          </button>
        </div>
      </div>

      <p className="report-description">
        Pre-registration manifest for issuing meal passes. Staff can use this list to distribute the correct passes to each camper.
      </p>

      {records.length === 0 ? (
        <div className="no-data">No meal tickets found.</div>
      ) : (
        <>
          <table className="meal-tickets-table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="qty-column">Qty</th>
                <th>Pass Type</th>
              </tr>
            </thead>
            <tbody>
              {records.map(record => (
                <tr key={record.id}>
                  <td>{record.lastName}, {record.firstName}</td>
                  <td className="qty-column">{record.quantity}</td>
                  <td>{record.passType}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="report-summary">
            <h3>Summary</h3>
            <div className="summary-grid">
              {Object.entries(totals).map(([passType, total]) => (
                <div key={passType} className="summary-item">
                  <span className="summary-label">{passType}:</span>
                  <span className="summary-value">{total}</span>
                </div>
              ))}
              <div className="summary-item total">
                <span className="summary-label">Total Passes:</span>
                <span className="summary-value">
                  {Object.values(totals).reduce((sum, val) => sum + val, 0)}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MealTickets;

